import { Reducer } from 'redux';
import {
  IScenario,
  IScenarioProps,
  IScenariosState,
  ScenarioActionTypes as types,
  SharingMode,
} from '../types/scenarios';

import {
  bauScenario,
  primaryScenario, // secondaryScenario available too
  secondaryScenario,
} from './../../data/scenarios';

// On first pass: just contain 1 key per scenario to simplify
// come up with generalized solution in future
const initialState: IScenariosState = {
  bau: bauScenario,
  primary: primaryScenario, // default to the business as usual case
  secondary: secondaryScenario,
};

const ModalSplitIntensities = new Set([
  'nonMotorizedTransit',
  'publicTransit',
  'sharedMobility',
]);

const ElectrificationGoals = new Set([
  "publicAndCommercial",
  "privateVehicle",
]);

const scenariosReducer: Reducer<IScenariosState> = (state = initialState, action) => {
  const { type, payload } = action;
  switch (type) {
    case types.SET_BOOLEAN_PROP:
    case types.SET_NUMBER_PROP:
      const { scenarioKey, propName, value } = payload;

      // A bit hacky, but it'll do for now
      // Exploits the fact none of the subkey names are reused
      let newScenario;

      const isModalSplitProp = ModalSplitIntensities.has(propName);
      const isElectrificationProp = ElectrificationGoals.has(propName);
      if (isModalSplitProp) {
        newScenario = {
          ...state[scenarioKey],
          modalSplitIntensity: {
            ...state[scenarioKey].modalSplitIntensity,
            [propName]: value,
          }
        }
      } else if (isElectrificationProp) { // same pattern as modal split reducer right above
        newScenario = {
          ...state[scenarioKey],
          electrificationVehicleGoal: {
            ...state[scenarioKey].electrificationVehicleGoal,
            [propName]: value,
          }
        }
      } else {
        newScenario = {
          ...state[scenarioKey],
          [propName]: value
        };
      }
      return { ...state, [scenarioKey]: newScenario };
    default:
      return state;
  }
}

export default scenariosReducer;

// Insulate against data structure change (switch from object to array, etc)
const scenarioByKey = (state: IScenariosState, props: IScenarioProps): IScenario => {
  return state[props.scenarioKey];
}

export const scenariosAsList = (state: IScenariosState): IScenario[] => {
  const scenarioKeys = ['bau', 'primary', 'secondary'];
  const scenarios: IScenario[] = [];
  scenarioKeys.forEach(key => {
    const scenario = state[key];
    if (scenario) {
      scenarios.push(scenario);
    }
  });
  return scenarios;
}

// DRY this up someday, e.g. if caller can pass a prop saying which one they actually want
export const getPrivateElectrificationVehicleGoal = (state: IScenariosState, props: IScenarioProps) => {
  return scenarioByKey(state, props).electrificationVehicleGoal.privateVehicle;
}

export const getPublicCommercialElectrificationVehicleGoal = (state: IScenariosState, props: IScenarioProps) => {
  return scenarioByKey(state, props).electrificationVehicleGoal.publicAndCommercial;
}

// Functions for extracting modalSplit... this could become a lone function.
export const getModalSplitNonMotorizedTransit = (state: IScenariosState, props: IScenarioProps) => {
  return scenarioByKey(state, props).modalSplitIntensity.nonMotorizedTransit;
}

export const getModalSplitPublicTransit = (state: IScenariosState, props: IScenarioProps) => {
  return scenarioByKey(state, props).modalSplitIntensity.publicTransit;
}

export const getModalSplitSharedMobility = (state: IScenariosState, props: IScenarioProps) => {
  return scenarioByKey(state, props).modalSplitIntensity.sharedMobility;
}

// This is really a selector...
export const getSharingModeFromScenario = (state: IScenario) => {
  const { sharedMobility } = state.modalSplitIntensity;
  return sharedMobilityLeverToSharingMode(sharedMobility);
}

export const sharedMobilityLeverToSharingMode = (intensity: number): SharingMode => {
  if (intensity >= 3) {
    return SharingMode.ExtremeSharing;
  }
  if (intensity === 2) {
    return SharingMode.Sharing;
  }
  return SharingMode.BAU;
}

// Get goal status
export const getHasRenewableEnergyTarget = (state: IScenariosState, props: IScenarioProps) => {
  return scenarioByKey(state, props).hasRenewableEnergyTarget;
}



// WEBPACK FOOTER //
// ./src/store/reducers/scenarios.ts